import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/AuthContext'
import {
  getMisSuscripciones, getDashboardResumen, getMisPedidos, getAhorroAcumulado,
  getHistorialPagos, getHistorialEntregas,
  pausarSuscripcion, reactivarSuscripcion, cambiarDiaSuscripcion,
  cambiarKitSuscripcion, cancelarMiSuscripcion,
  getKitsDisponibles, getCuentaCobroPublica, getKitsConProductos,
} from '../../lib/portalClienteApi'

const ESTADO_PILL = {
  activa:    { bg: '#dcfce7', color: '#15803d', label: 'Activa' },
  pausada:   { bg: '#fef3c7', color: '#92400e', label: 'Pausada' },
  cancelada: { bg: '#fee2e2', color: '#991b1b', label: 'Cancelada' },
}

export default function ClienteDashboard() {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const pagoStatus = params.get('pago')        // 'ok' | 'falla' | 'pendiente'
  const esVisitorPago = params.get('visitor') === '1'
  const [emailVisitor] = useState(() => {
    try { return localStorage.getItem('limpack_visitor_email') } catch { return null }
  })
  const [suscripciones, setSuscripciones] = useState([])
  const [pedidos, setPedidos] = useState([])
  const [loading, setLoading] = useState(true)
  const [expandida, setExpandida] = useState(null)
  const [accion, setAccion] = useState(null)
  const [cuenta, setCuenta] = useState(null)
  const [ahorro, setAhorro] = useState(null)
  const [resumenes, setResumenes] = useState({})  // {[susc_id]: resumen}
  const [tab, setTab] = useState('suscripcion')   // 'suscripcion' | 'sumar' | 'compras'

  useEffect(() => {
    if (!user?.id) return
    cargar()
    getCuentaCobroPublica().then(setCuenta)
  }, [user?.id])

  async function cargar() {
    setLoading(true)
    const [data, peds, ah] = await Promise.all([
      getMisSuscripciones(user.id),
      getMisPedidos(10),
      getAhorroAcumulado(user.id),
    ])
    setSuscripciones(data)
    setPedidos(peds)
    setAhorro(ah)
    // Pre-cargar resumenes de cada suscripcion para los stats arriba
    const resObj = {}
    for (const s of data) {
      try { resObj[s.id] = await getDashboardResumen(s.id) } catch {}
    }
    setResumenes(resObj)
    setLoading(false)
    if (data.length === 1) setExpandida(data[0].id)
  }

  async function logout() {
    await supabase.auth.signOut()
    navigate('/login', { replace: true })
  }

  const nombre = profile?.nombre?.split(' ')[0] || 'Hola'
  const tieneSusc = suscripciones.length > 0
  const activas = suscripciones.filter(s => s.estado === 'activa').length

  // Calcular stats globales (de la suscripcion principal — primera activa)
  const suscPrincipal = suscripciones.find(s => s.estado === 'activa') || suscripciones[0]
  const resumenPrincipal = suscPrincipal ? resumenes[suscPrincipal.id] : null
  const ahorroTotalNum = Math.round(Number(ahorro?.ahorro_total || 0))
  const ahorroEsteMes = Math.round(Number(ahorro?.ahorro_total || 0))  // por ahora total = mes
  const proxEntregaFecha = resumenPrincipal?.proximaEntregaReal || resumenPrincipal?.proximaEntregaCalc
  const proxCobroMonto = resumenPrincipal?.proximoPago?.monto || resumenPrincipal?.bonif?.montoProximo || (suscPrincipal?.kit?.precio_susc)
  const proxCobroFecha = resumenPrincipal?.proximoPago?.vence || resumenPrincipal?.bonif?.proximaFechaCobro

  return (
    <div className="cli-app">
      {/* NAVBAR (mismo lenguaje que Wizard / La Tienda) */}
      <nav className="cli-nav">
        <div className="cli-nav-inner">
          <Link to="/cliente" className="cli-logo">
            <img src="/isotipo.png" alt="" className="cli-logo-iso" />
            <span>Lim<em>pack</em></span>
          </Link>
          <div className="cli-nav-actions">
            <Link to="/cliente/perfil" className="cli-nav-link">Mi perfil</Link>
            <button onClick={logout} className="cli-nav-logout">Salir</button>
          </div>
        </div>
      </nav>

      {/* HERO con saludo + frase de ahorro */}
      <section className="cli-hero">
        <div className="cli-hero-inner">
          <div className="cli-hero-pill">
            <span className="cli-hero-dot"></span>
            Mi cuenta
          </div>
          <h1 className="cli-hero-h1">
            👋 Hola, <span>{nombre}</span>
          </h1>
          <p className="cli-hero-sub">
            {loading
              ? 'Cargando tu cuenta…'
              : !tieneSusc
                ? 'Todavía no armaste tu primera suscripción. Lo hacemos en 2 minutos.'
                : (
                  <>
                    Tenés <strong>{activas} suscripción{activas === 1 ? '' : 'es'} activa{activas === 1 ? '' : 's'}</strong>
                    {ahorroTotalNum > 0 && <> · Llevás <strong>${ahorroTotalNum.toLocaleString('es-UY')}</strong> ahorrado vs supermercado</>}
                    . <em style={{ opacity: .85 }}>Seguí comprando para tener más ahorro.</em>
                  </>
                )}
          </p>
        </div>
      </section>

      <main className="cli-main">
        {/* Banner post-pago MP (visitor o logueado) */}
        {pagoStatus && (
          <div style={{
            marginBottom: 20, padding: '16px 18px',
            background: pagoStatus === 'ok' ? '#ecfdf5' : pagoStatus === 'falla' ? '#fee2e2' : '#fef3c7',
            border: `1px solid ${pagoStatus === 'ok' ? '#a7f3d0' : pagoStatus === 'falla' ? '#fca5a5' : '#fde68a'}`,
            borderRadius: 12,
            color: pagoStatus === 'ok' ? '#065f46' : pagoStatus === 'falla' ? '#991b1b' : '#78350f',
            fontSize: 14, lineHeight: 1.5,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          }}>
            <div style={{ flex: 1 }}>
              {pagoStatus === 'ok' && (
                <>
                  <strong>✅ ¡Pago aprobado!</strong>
                  {esVisitorPago && emailVisitor && (
                    <> Te mandamos un email a <strong>{emailVisitor}</strong> — confirmá tu cuenta para ver tus pedidos.</>
                  )}
                  {!esVisitorPago && <> Tu pedido ya está confirmado. Te avisamos cuando salga el repartidor.</>}
                </>
              )}
              {pagoStatus === 'falla' && (
                <><strong>❌ El pago no se pudo procesar.</strong> Probá de nuevo desde tu pedido pendiente.</>
              )}
              {pagoStatus === 'pendiente' && (
                <><strong>⏳ Pago en proceso.</strong> Te avisamos cuando se acredite (puede tardar hasta 48hs si elegiste Abitab/RedPagos).</>
              )}
            </div>
            <button
              type="button"
              onClick={() => { setParams({}); try { localStorage.removeItem('limpack_visitor_email') } catch {} }}
              style={{
                background: 'transparent', border: 0, fontSize: 18, cursor: 'pointer',
                color: 'inherit', opacity: .6, padding: '4px 8px',
              }}>
              ✕
            </button>
          </div>
        )}

        {loading ? (
          <div className="cli-loading">Cargando…</div>
        ) : !tieneSusc ? (
          <CardVacio nombre={nombre} />
        ) : (
          <>
            {/* Stats arriba: 3 cards rápidas */}
            <div className="cli-stats">
              <div className="cli-stat">
                <div className="cli-stat-tag">📦 Próxima entrega · Kit</div>
                <div className="cli-stat-big">
                  {proxEntregaFecha
                    ? new Date(proxEntregaFecha + 'T12:00:00').toLocaleDateString('es-UY', { weekday: 'short', day: 'numeric', month: 'short' })
                    : '—'}
                </div>
                <div className="cli-stat-sub">{suscPrincipal?.ventana_horaria === 'tarde' ? 'Tarde 14-18hs' : 'Mañana 10-14hs'}</div>
              </div>
              <div className="cli-stat is-warning">
                <div className="cli-stat-tag">💰 Próximo cobro · Suscripción</div>
                <div className="cli-stat-big">${Number(proxCobroMonto || 0).toLocaleString('es-UY')}</div>
                <div className="cli-stat-sub">
                  {proxCobroFecha
                    ? <>Vence {new Date(proxCobroFecha + 'T12:00:00').toLocaleDateString('es-UY', { day: 'numeric', month: 'short' })} · IVA incluido</>
                    : 'IVA incluido'}
                  {suscPrincipal?.mp_preapproval_status === 'authorized' && <> · 🔒 Débito MP automático</>}
                </div>
              </div>
              <div className="cli-stat is-savings">
                <div className="cli-stat-tag">💸 Ahorrado este mes</div>
                <div className="cli-stat-big">${ahorroEsteMes.toLocaleString('es-UY')}</div>
                <div className="cli-stat-sub">Año: ${ahorroTotalNum.toLocaleString('es-UY')} acumulado</div>
              </div>
            </div>

            {/* Vecinos en tu cuadra (visible siempre arriba, debajo de stats) */}
            {resumenPrincipal?.vecinos && (
              <div style={{
                marginBottom: 20, padding: '14px 18px',
                background: resumenPrincipal.vecinos.cuadra >= resumenPrincipal.vecinos.paraDescuento ? '#ecfdf5' : '#fff',
                border: `1px solid ${resumenPrincipal.vecinos.cuadra >= resumenPrincipal.vecinos.paraDescuento ? '#10b981' : '#e2e8f0'}`,
                borderRadius: 14,
                display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
                boxShadow: '0 4px 16px rgba(26,52,97,.04)',
              }}>
                <div style={{ flex: '1 1 240px' }}>
                  <div style={{ fontWeight: 700, color: '#1a3461', fontSize: 14 }}>🏘️ Vecinos en tu cuadra</div>
                  <div style={{ fontSize: 13, color: '#64748b', marginTop: 2, lineHeight: 1.4 }}>
                    {resumenPrincipal.vecinos.cuadra >= resumenPrincipal.vecinos.paraDescuento
                      ? <>✅ ¡Ya tenés el <strong>5% de descuento</strong> vecinal!</>
                      : <>Hay <strong>{resumenPrincipal.vecinos.cuadra}</strong> {resumenPrincipal.vecinos.cuadra === 1 ? 'vecino suscripto' : 'vecinos suscriptos'}. Si llegan a <strong>{resumenPrincipal.vecinos.paraDescuento}</strong>, todos ganan <strong>5% de descuento</strong>.</>
                    }
                  </div>
                  <div style={{ marginTop: 8, height: 6, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{
                      width: `${Math.min(100, (resumenPrincipal.vecinos.cuadra / resumenPrincipal.vecinos.paraDescuento) * 100)}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #00c4d4, #15803d)',
                    }}></div>
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, fontWeight: 600 }}>
                    {resumenPrincipal.vecinos.cuadra} / {resumenPrincipal.vecinos.paraDescuento}
                  </div>
                </div>
                {resumenPrincipal.vecinos.cuadra < resumenPrincipal.vecinos.paraDescuento && (
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(`Hola! Me sumé a Limpack (productos de limpieza por suscripción). Si nos suscribimos ${resumenPrincipal.vecinos.paraDescuento}+ vecinos en la cuadra todos tenemos 5% de descuento permanente. Mirá: https://limpack.uy`)}`}
                    target="_blank" rel="noopener"
                    style={{
                      padding: '10px 18px', background: '#22c55e', color: '#fff',
                      borderRadius: 10, fontWeight: 700, fontSize: 13,
                      textDecoration: 'none', whiteSpace: 'nowrap',
                    }}
                  >
                    📤 Invitar vecinos por WA
                  </a>
                )}
              </div>
            )}

            {/* Tabs */}
            <div className="cli-tabs">
              <button className={`cli-tab ${tab === 'suscripcion' ? 'is-active' : ''}`} onClick={() => setTab('suscripcion')}>
                🎁 Mi Suscripción
              </button>
              <button className={`cli-tab ${tab === 'sumar' ? 'is-active' : ''}`} onClick={() => setTab('sumar')}>
                ➕ Sumar Kits
              </button>
              <button className={`cli-tab ${tab === 'compras' ? 'is-active' : ''}`} onClick={() => setTab('compras')}>
                🛒 Compras puntuales
              </button>
            </div>

            <div className="cli-tab-content">
              {tab === 'suscripcion' && (
                <div className="cli-list">
                  {suscripciones.map(s => (
                    <SuscripcionCard
                      key={s.id}
                      suscripcion={s}
                      expandida={expandida === s.id}
                      onToggle={() => setExpandida(expandida === s.id ? null : s.id)}
                      onAccion={(tipo) => setAccion({ tipo, suscripcion: s })}
                      userId={user.id}
                      ahorro={ahorro}
                    />
                  ))}
                </div>
              )}

              {tab === 'sumar' && <SumarKitsTab />}

              {tab === 'compras' && <ComprasPuntualesTab pedidos={pedidos} />}
            </div>
          </>
        )}
      </main>

      {accion && (
        <ModalAccion
          accion={accion}
          onClose={() => setAccion(null)}
          onDone={() => { setAccion(null); cargar() }}
        />
      )}

      {/* WhatsApp flotante */}
      <a href="https://wa.me/59898610001?text=Hola!%20Tengo%20una%20duda%20sobre%20mi%20cuenta"
         target="_blank" rel="noopener" className="cli-wa" aria-label="Contactar por WhatsApp">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>

      {/* FOOTER (igual al de Wizard) */}
      <footer className="cli-footer">
        <div className="cli-footer-inner">
          <div className="cli-footer-grid">
            <div>
              <div className="cli-footer-logo">Lim<span>pack</span></div>
              <p className="cli-footer-desc">Productos de limpieza a domicilio por suscripción. Montevideo, Uruguay.</p>
            </div>
            <div>
              <div className="cli-footer-tit">Navegación</div>
              <ul className="cli-footer-list">
                <li><a href="https://limpack.uy/#como" target="_blank" rel="noopener">Cómo funciona</a></li>
                <li><a href="https://limpack.uy/#kits" target="_blank" rel="noopener">Nuestros kits</a></li>
                <li><a href="https://limpack.uy/#despensa" target="_blank" rel="noopener">La Tienda Limpack</a></li>
                <li><a href="https://limpack.uy/#barrios" target="_blank" rel="noopener">Zonas de entrega</a></li>
              </ul>
            </div>
            <div>
              <div className="cli-footer-tit">Mi cuenta</div>
              <ul className="cli-footer-list">
                <li><Link to="/cliente">Mis suscripciones</Link></li>
                <li><Link to="/cliente/perfil">Mi perfil</Link></li>
                <li><a href="https://wa.me/59898610001" target="_blank" rel="noopener">WhatsApp</a></li>
                <li><a href="mailto:hola@limpack.uy">hola@limpack.uy</a></li>
              </ul>
            </div>
          </div>
          <div className="cli-footer-bot">
            <p>© 2026 Limpack. Todos los derechos reservados.</p>
            <p>Montevideo, Uruguay · IVA incluido</p>
          </div>
        </div>
      </footer>

      <style>{styles}</style>
    </div>
  )
}

/* ───────── Card "sin suscripciones" — 3 opciones de empezar ───────── */
function CardVacio({ nombre }) {
  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <img src="/isotipo.png" alt="" style={{ width: 64, height: 64, marginBottom: 12 }} />
        <h2 style={{ color: '#1a3461', margin: '0 0 8px', fontSize: 26 }}>¡Bienvenido a Limpack, {nombre}!</h2>
        <p style={{ color: '#64748b', margin: 0, fontSize: 15 }}>
          Elegí cómo querés empezar. Cualquier opción te lleva 2 minutos.
        </p>
      </div>

      <div className="cli-extras-grid">
        <Link to="/cliente/nueva-suscripcion" className="cli-extras-card cli-extras-card-3" style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', top: 12, right: 12, background: '#00c4d4', color: '#1a3461', fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 999, letterSpacing: '.04em' }}>
            ⭐ MÁS ELEGIDO
          </div>
          <div className="cli-extras-icon">🎁</div>
          <div className="cli-extras-titulo">Suscribirme a un kit</div>
          <div className="cli-extras-desc">Cada mes te llega lo justo para tu casa. Cobro automático, sin pensarlo.</div>
          <div className="cli-extras-cta">Empezar →</div>
        </Link>
        <Link to="/cliente/probar-kit" className="cli-extras-card cli-extras-card-2">
          <div className="cli-extras-icon">📦</div>
          <div className="cli-extras-titulo">Probar un kit (una vez)</div>
          <div className="cli-extras-desc">Un kit completo por única vez, sin compromiso. Para probar.</div>
          <div className="cli-extras-cta">Ver kits →</div>
        </Link>
        <Link to="/cliente/tienda" className="cli-extras-card cli-extras-card-1">
          <div className="cli-extras-icon">🛍️</div>
          <div className="cli-extras-titulo">Comprar en la Tienda</div>
          <div className="cli-extras-desc">Productos sueltos, lo que necesites cuando lo necesites.</div>
          <div className="cli-extras-cta">Ir a la tienda →</div>
        </Link>
      </div>

      <p style={{ textAlign: 'center', marginTop: 20, color: '#64748b', fontSize: 13 }}>
        ¿Dudas? Escribinos al WhatsApp{' '}
        <a href="https://wa.me/59898610001" style={{ color: '#1a3461', fontWeight: 700 }}>098 610 001</a>.
      </p>
    </div>
  )
}

/* ───────── Bloque "Mis pedidos" ───────── */
function MisPedidosBlock({ pedidos }) {
  const [expandido, setExpandido] = useState(null)
  if (!pedidos || pedidos.length === 0) return null
  const fmt = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('es-UY', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'
  const fmtLargo = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'long' }) : '—'
  const ESTADO_PILL = {
    borrador:        { bg: '#f1f5f9', color: '#64748b', label: 'Borrador' },
    pendiente_pago:  { bg: '#fef3c7', color: '#92400e', label: 'Pendiente pago' },
    pagado:          { bg: '#dbeafe', color: '#1e40af', label: 'Pagado' },
    en_armado:       { bg: '#e0e7ff', color: '#3730a3', label: 'En armado' },
    en_ruta:         { bg: '#cffafe', color: '#0e7490', label: 'En ruta' },
    entregado:       { bg: '#dcfce7', color: '#15803d', label: 'Entregado' },
    cancelado:       { bg: '#fee2e2', color: '#991b1b', label: 'Cancelado' },
    rechazado:       { bg: '#fee2e2', color: '#991b1b', label: 'Rechazado' },
  }
  return (
    <div className="cli-pedidos-block">
      <h2>Histórico</h2>
      <div className="cli-pedidos-list">
        {pedidos.map(p => {
          const pill = ESTADO_PILL[p.estado] || ESTADO_PILL.pendiente_pago
          const items = Array.isArray(p.items) ? p.items : []
          const resumen = p.kit_codigo
            ? `Kit ${p.kit_codigo.replace(/_/g, ' ')}`
            : items.length === 1
              ? `${items[0].cantidad}× ${items[0].nombre}`
              : `${items.reduce((s, it) => s + Number(it.cantidad || 1), 0)} productos`
          const isOpen = expandido === p.id
          return (
            <div key={p.id} className={`cli-pedido ${isOpen ? 'is-open' : ''}`}>
              <button type="button" className="cli-pedido-head" onClick={() => setExpandido(isOpen ? null : p.id)}>
                <div className="cli-pedido-info">
                  <div className="cli-pedido-num">{p.numero_orden}</div>
                  <div className="cli-pedido-resumen">{resumen}</div>
                  <div className="cli-pedido-fecha">📅 Entrega {fmt(p.fecha_entrega_programada)}</div>
                </div>
                <div className="cli-pedido-right">
                  <span className="cli-pill" style={{ background: pill.bg, color: pill.color }}>{pill.label}</span>
                  <div className="cli-pedido-monto">${Number(p.monto_total).toLocaleString('es-UY')}</div>
                  <div className="cli-pedido-toggle">{isOpen ? '▴' : '▾'}</div>
                </div>
              </button>

              {isOpen && (
                <div className="cli-pedido-body">
                  {/* Items / Kit */}
                  <div className="cli-pedido-section">
                    <div className="cli-pedido-section-tag">📦 Qué pediste</div>
                    {p.kit_codigo ? (
                      <div className="cli-pedido-kit">
                        <strong>Kit {p.kit_codigo.replace(/_/g, ' ')}</strong> · 1 unidad
                      </div>
                    ) : items.length > 0 ? (
                      <ul className="cli-pedido-items">
                        {items.map((it, i) => (
                          <li key={i}>
                            <span className="cli-pedido-item-qty">{it.cantidad}×</span>
                            <span className="cli-pedido-item-name">
                              {it.nombre}
                              {it.presentacion && <span className="cli-pedido-item-pres"> · {it.presentacion}</span>}
                            </span>
                            <span className="cli-pedido-item-precio">${Number(it.precio_unit * it.cantidad).toLocaleString('es-UY')}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div style={{ fontSize: 12, color: 'var(--muted2)' }}>Sin detalle disponible</div>
                    )}
                  </div>

                  {/* Entrega */}
                  <div className="cli-pedido-section">
                    <div className="cli-pedido-section-tag">📍 Entrega</div>
                    <div className="cli-pedido-meta">
                      <strong>{fmtLargo(p.fecha_entrega_programada)}</strong> · {p.entrega_ventana === 'tarde' ? 'Tarde 14-18hs' : 'Mañana 10-14hs'}<br/>
                      {p.entrega_direccion}{p.entrega_apto ? ` · ${p.entrega_apto}` : ''}
                    </div>
                  </div>

                  {/* Totales */}
                  <div className="cli-pedido-section">
                    <div className="cli-pedido-totales">
                      <div><span>Subtotal</span><span>${Number(p.monto_subtotal).toLocaleString('es-UY')}</span></div>
                      <div><span>Envío</span><span>{Number(p.costo_envio) > 0 ? `$${Number(p.costo_envio).toLocaleString('es-UY')}` : 'Gratis'}</span></div>
                      <div className="cli-pedido-total"><span>Total · IVA incluido</span><span>${Number(p.monto_total).toLocaleString('es-UY')}</span></div>
                    </div>
                  </div>

                  {/* Si pendiente: cómo pagar */}
                  {p.estado === 'pendiente_pago' && (
                    <div className="cli-pedido-pagar">
                      ⚠️ <strong>Esperando tu pago.</strong> Te mandamos los datos de transferencia por WhatsApp. Si no los recibiste, escribinos al{' '}
                      <a href="https://wa.me/59898610001" target="_blank" rel="noopener">098 610 001</a>.
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ───────── Tab "Sumar Kits" ───────── */
function SumarKitsTab() {
  return (
    <div className="cli-tab-pane">
      <h2 className="cli-pane-h2">Sumar otra suscripción</h2>
      <p className="cli-pane-sub">Para tu mamá, la oficina, casa de la playa — cada dirección con su propio kit y entregas separadas.</p>
      <div className="cli-extras-grid">
        <Link to="/cliente/nueva-suscripcion" className="cli-extras-card cli-extras-card-3">
          <div className="cli-extras-icon">🔄</div>
          <div className="cli-extras-titulo">Otra suscripción mensual</div>
          <div className="cli-extras-desc">Un nuevo kit para otra dirección. Cobro automático con MP cada mes.</div>
          <div className="cli-extras-cta">Armar otra →</div>
        </Link>
        <Link to="/cliente/probar-kit" className="cli-extras-card cli-extras-card-2">
          <div className="cli-extras-icon">📦</div>
          <div className="cli-extras-titulo">Probar un kit (una vez)</div>
          <div className="cli-extras-desc">Un kit completo por única vez, sin compromiso. Para regalo o probar.</div>
          <div className="cli-extras-cta">Ver kits →</div>
        </Link>
      </div>
    </div>
  )
}

/* ───────── Tab "Compras puntuales" ───────── */
function ComprasPuntualesTab({ pedidos }) {
  return (
    <div className="cli-tab-pane">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 className="cli-pane-h2" style={{ margin: 0 }}>Compras puntuales</h2>
          <p className="cli-pane-sub" style={{ margin: '4px 0 0' }}>Productos sueltos que pediste fuera de tu suscripción.</p>
        </div>
        <Link to="/cliente/tienda" className="cli-cta" style={{ padding: '10px 20px', fontSize: 14 }}>
          🛍️ Ir a la Tienda →
        </Link>
      </div>
      {(!pedidos || pedidos.length === 0) ? (
        <div style={{ textAlign: 'center', padding: 40, background: '#f9fafb', borderRadius: 12, color: '#6b7280' }}>
          Todavía no hiciste compras puntuales. <Link to="/cliente/tienda" style={{ color: '#1a3461', fontWeight: 700 }}>Visitá la Tienda →</Link>
        </div>
      ) : (
        <MisPedidosBlock pedidos={pedidos} />
      )}
    </div>
  )
}

/* ───────── Bloque "Comprá extras" (legacy, queda para uso interno si hace falta) ───────── */
function ExtrasBlock() {
  return (
    <div className="cli-extras">
      <h2 className="cli-extras-h2">¿Querés algo más?</h2>
      <p className="cli-extras-sub">Tres formas de sumarle a tu suscripción mensual.</p>
      <div className="cli-extras-grid">
        <Link to="/cliente/tienda" className="cli-extras-card cli-extras-card-1">
          <div className="cli-extras-icon">🛍️</div>
          <div className="cli-extras-titulo">Productos sueltos</div>
          <div className="cli-extras-desc">Pedí lo que te falte cuando lo necesites. Envío gratis cuando pasamos por tu cuadra.</div>
          <div className="cli-extras-cta">Ir a la tienda →</div>
        </Link>
        <Link to="/cliente/probar-kit" className="cli-extras-card cli-extras-card-2">
          <div className="cli-extras-icon">📦</div>
          <div className="cli-extras-titulo">Pedir otro kit</div>
          <div className="cli-extras-desc">Un kit completo por única vez, sin suscribirte. Para regalo o casa de fin de semana.</div>
          <div className="cli-extras-cta">Ver kits →</div>
        </Link>
        <Link to="/cliente/nueva-suscripcion" className="cli-extras-card cli-extras-card-3">
          <div className="cli-extras-icon">🔄</div>
          <div className="cli-extras-titulo">Otra suscripción</div>
          <div className="cli-extras-desc">Para tu mamá, la oficina, o tu casa de la playa. Cada dirección con su propio kit.</div>
          <div className="cli-extras-cta">Armar otra →</div>
        </Link>
      </div>
    </div>
  )
}

/* ───────── Tarjeta de suscripción ───────── */
function SuscripcionCard({ suscripcion: s, expandida, onToggle, onAccion, userId, ahorro }) {
  const [resumen, setResumen] = useState(null)
  const [pagos, setPagos] = useState([])
  const [entregas, setEntregas] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [productosKit, setProductosKit] = useState(null)
  const [verProductos, setVerProductos] = useState(false)

  useEffect(() => {
    if (!expandida || loaded) return
    Promise.all([
      getDashboardResumen(s.id),
      getHistorialPagos(s.id, 6),
      getHistorialEntregas(s.id, 6),
    ]).then(([res, ps, es]) => {
      setResumen(res); setPagos(ps); setEntregas(es); setLoaded(true)
    })
  }, [expandida, s.id])

  // Cargar productos del kit la primera vez que se abre el desplegable
  useEffect(() => {
    if (!verProductos || productosKit !== null) return
    getKitsConProductos().then(kits => {
      const k = kits.find(x => x.codigo === s.kit_actual)
      setProductosKit(k?.productos || [])
    }).catch(() => setProductosKit([]))
  }, [verProductos, s.kit_actual])

  const pill = ESTADO_PILL[s.estado] || ESTADO_PILL.activa
  const esBeneficiario = s.beneficiario_id === userId
  const esTitular = s.titular_id === userId

  // Datos enriquecidos del resumen
  const proxEntregaFecha = resumen?.proximaEntregaReal || resumen?.proximaEntregaCalc
  const proxEntregaEsCalc = !resumen?.proximaEntregaReal && !!resumen?.proximaEntregaCalc
  const corteISO = resumen?.proximaEntregaCorte
  const horasACorte = corteISO ? Math.round((new Date(corteISO).getTime() - Date.now()) / 36e5) : null
  const corteVencido = horasACorte !== null && horasACorte < 0

  return (
    <div className={`cli-card ${expandida ? 'is-open' : ''}`}>
      {/* Header */}
      <button type="button" onClick={onToggle} className="cli-card-head">
        <div className="cli-card-head-left">
          {s.alias && <div className="cli-alias">📍 {s.alias.toUpperCase()}</div>}
          <h3 className="cli-card-titulo">{s.kit?.nombre || 'Sin kit'}</h3>
          <div className="cli-card-direccion">
            {s.barrio_obj?.nombre || s.barrio} · {s.direccion}{s.apto ? ` · ${s.apto}` : ''}
          </div>
          {esBeneficiario && !esTitular && (
            <div className="cli-card-beneficio">🎁 Suscripción de {s.titular?.nombre}</div>
          )}
        </div>
        <div className="cli-card-head-right">
          <span className="cli-pill" style={{ background: pill.bg, color: pill.color }}>{pill.label}</span>
          <div className="cli-card-precio">
            ${Number(s.kit?.precio_susc || 0).toLocaleString('es-UY')}
            <span>/mes</span>
            <div style={{ fontSize: 10, fontWeight: 500, color: '#6b7280', marginTop: 2, lineHeight: 1.2 }}>
              IVA incluido{resumen?.bonif?.aplica && <> · desde el mes 4</>}
            </div>
          </div>
          <div className="cli-card-toggle">{expandida ? '▴' : '▾'}</div>
        </div>
      </button>

      {expandida && (
        <div className="cli-card-body">
          {/* Desplegable: Ver que incluye (estilo link simple) */}
          <div style={{ marginBottom: 12 }}>
            <button
              type="button"
              onClick={() => setVerProductos(v => !v)}
              style={{
                background: 'transparent', border: 0, padding: '6px 0',
                color: '#1a5cb8', fontWeight: 700, fontSize: 14,
                cursor: 'pointer', font: 'inherit',
                textDecoration: 'none',
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}>
              {verProductos ? 'Ocultar productos' : 'Ver qué incluye'} {verProductos ? '▴' : '▾'}
            </button>
            {verProductos && (
              <div style={{ marginTop: 8, padding: 16, background: '#f9fafb', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                {productosKit === null ? (
                  <div style={{ color: '#64748b', fontSize: 13, textAlign: 'center', padding: 20 }}>Cargando…</div>
                ) : productosKit.length === 0 ? (
                  <div style={{ color: '#64748b', fontSize: 13, textAlign: 'center', padding: 20 }}>No hay productos cargados todavía.</div>
                ) : (
                  <>
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 0 }}>
                    {productosKit.map((p, i) => (
                      <li key={i} style={{
                        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                        padding: '8px 0',
                        borderBottom: i < productosKit.length - 1 ? '1px solid #e5e7eb' : 'none',
                        fontSize: 13, color: '#374151',
                      }}>
                        <span style={{
                          background: '#1a3461', color: '#fff', fontWeight: 700,
                          minWidth: 28, padding: '2px 8px', borderRadius: 6,
                          fontSize: 12, textAlign: 'center',
                        }}>{p.cantidad || 1}×</span>
                        <strong style={{ color: '#1a3461' }}>{p.nombre}</strong>
                        {p.presentacion && <em style={{ color: '#64748b', fontStyle: 'normal' }}>· {p.presentacion}</em>}
                        {p.marca && <span style={{ marginLeft: 'auto', fontSize: 11, color: '#64748b', fontWeight: 600 }}>{p.marca}</span>}
                      </li>
                    ))}
                  </ul>
                  <div style={{
                    marginTop: 12, padding: '10px 12px',
                    background: '#fffbeb', border: '1px solid #fcd34d',
                    borderRadius: 8, fontSize: 11, color: '#78350f', lineHeight: 1.5,
                  }}>
                    ⚠️ Las marcas pueden cambiar según stock. Si no hay stock de una marca específica, la reemplazamos por una <strong>alternativa de igual calidad y precio</strong>, testeada por nosotros.
                  </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* (Bloques de proxima entrega/cobro/ahorro estan arriba en stats globales) */}
          <div style={{ display: 'none' }}>{/* legacy */}
            <div className="cli-block">
              <div className="cli-block-tag">📦 Próxima entrega</div>
              {proxEntregaFecha ? (
                <>
                  <div className="cli-block-big">{formatFecha(proxEntregaFecha)}</div>
                  <div className="cli-block-sub">
                    {s.ventana_horaria === 'tarde' ? 'Tarde 14-18hs' : 'Mañana 10-14hs'}
                  </div>
                  {proxEntregaEsCalc && (
                    <div className="cli-block-extra-info">📅 Fecha estimada según tu día asignado</div>
                  )}
                  {horasACorte !== null && !corteVencido && (
                    <div className={`cli-corte ${horasACorte < 48 ? 'is-urgent' : ''}`}>
                      🕐 Cierre {horasACorte < 48 ? `en ${horasACorte}hs` : `el ${formatFechaCorta(corteISO.slice(0, 10))}`} · Hasta entonces podés modificar
                    </div>
                  )}
                </>
              ) : (
                <div className="cli-block-placeholder">Sin entrega programada todavía</div>
              )}
            </div>
            <div className={`cli-block ${resumen?.proximoPago ? 'is-warning' : 'is-ok'}`}>
              <div className="cli-block-tag">💰 {(resumen?.proximoPago || resumen?.bonif?.aplica) ? 'Próximo cobro' : '✓ Al día'}</div>
              {resumen?.proximoPago ? (
                <>
                  <div className="cli-block-big">${resumen.proximoPago.monto.toLocaleString('es-UY')}</div>
                  <div className="cli-block-sub">
                    {resumen.proximoPago.estado === 'atrasado' ? '⚠️ Atrasado' : `Vence ${formatFecha(resumen.proximoPago.vence)}`} · IVA incluido
                    {s.mp_preapproval_status === 'authorized' && <> · 🔒 Débito MP automático</>}
                  </div>
                  {!s.mp_preapproval_id && esTitular && (
                    <button className="cli-btn-pagar" onClick={() => onAccion('pagar')}>Cómo pagar →</button>
                  )}
                </>
              ) : resumen?.bonif?.aplica ? (
                <>
                  <div className="cli-block-big">${resumen.bonif.montoProximo.toLocaleString('es-UY')}</div>
                  <div className="cli-block-sub">
                    Próximo cobro · Mes {resumen.bonif.mesActual} de 3 · IVA incluido
                    {(resumen.bonif.mesActual === 2 || resumen.bonif.mesActual === 3) && ' · con descuento ✨'}
                    {s.mp_preapproval_status === 'authorized' && <> · 🔒 Débito MP automático</>}
                  </div>
                </>
              ) : (
                <div className="cli-block-placeholder">Sin pagos pendientes</div>
              )}
            </div>

            {/* 💸 Ahorro total vs supermercado (suscripción + tienda + kits one-shot) */}
            {(() => {
              const ahorroTotal = Math.round(Number(ahorro?.ahorro_total || 0))
              const totalPagado = Math.round(Number(ahorro?.total_pagado || 0))
              const compras = Number(ahorro?.pagos_susc_count || 0) + Number(ahorro?.pedidos_count || 0)
              if (ahorroTotal > 0) {
                return (
                  <div className="cli-block is-savings">
                    <div className="cli-block-tag">💸 Ya te ahorraste</div>
                    <div className="cli-block-big">${ahorroTotal.toLocaleString('es-UY')}</div>
                    <div className="cli-block-sub">
                      En {compras} {compras === 1 ? 'compra' : 'compras'} · vs el súper
                    </div>
                  </div>
                )
              }
              // Si todavía no pagó nada
              const precioMensual = Number(s.kit?.precio_susc || 0)
              const ahorroMensual = Math.round(precioMensual * 0.111)
              return (
                <div className="cli-block is-savings">
                  <div className="cli-block-tag">💸 Vas a ahorrar</div>
                  <div className="cli-block-big">${ahorroMensual.toLocaleString('es-UY')}<span style={{ fontSize: 14, fontWeight: 600, opacity: .7 }}> /mes</span></div>
                  <div className="cli-block-sub">solo con la suscripción · y suma cada compra en la Tienda</div>
                </div>
              )
            })()}
          </div>

          {/* Pago automático MP */}
          {esTitular && s.mp_preapproval_id && s.mp_preapproval_status === 'authorized' && (
            <div style={{
              marginTop: 12, padding: '12px 14px',
              background: '#ecfdf5', border: '1px solid #10b981',
              borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: '#065f46' }}>
                  ✅ Cobro automático activo
                </div>
                <div style={{ fontSize: 12, marginTop: 2, color: '#047857' }}>
                  {s.mp_card_last4
                    ? <>{s.mp_card_brand?.toUpperCase() || 'Tarjeta'} ···· {s.mp_card_last4} · Te cobramos solos cada mes</>
                    : <>Mercado Pago · Te cobramos solos cada mes</>
                  }
                </div>
              </div>
              <Link to={`/cliente/cargar-tarjeta/${s.id}`}
                style={{
                  padding: '8px 14px', background: '#fff', color: '#1e293b',
                  border: '1px solid #d1d5db',
                  borderRadius: 8, fontWeight: 600, fontSize: 13, textDecoration: 'none', whiteSpace: 'nowrap'
                }}>
                Cambiar tarjeta
              </Link>
            </div>
          )}
          {esTitular && (!s.mp_preapproval_id || s.mp_preapproval_status !== 'authorized') && (
            <div style={{
              marginTop: 12, padding: '12px 14px',
              background: '#fef2f2', border: '1px solid #fca5a5',
              borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: '#991b1b' }}>
                  ⚠️ Cobro automático no activado
                </div>
                <div style={{ fontSize: 12, marginTop: 2, color: '#b91c1c' }}>
                  Activalo para que tu kit no se atrase. Te cobramos solos cada mes.
                </div>
              </div>
              <Link to={`/cliente/cargar-tarjeta/${s.id}`}
                style={{
                  padding: '8px 14px', background: '#1e293b', color: '#fff',
                  border: 'none',
                  borderRadius: 8, fontWeight: 600, fontSize: 13, textDecoration: 'none', whiteSpace: 'nowrap'
                }}>
                Activar →
              </Link>
            </div>
          )}

          {/* Bonificación 3 meses */}
          {resumen?.bonif?.aplica && (() => {
            const m1 = Number(resumen.bonif.montoFull)
            const m23 = Number(resumen.bonif.montoNormal) - Number(resumen.bonif.ahorroTotal)/2
            const total3meses = m1 + m23 * 2
            const precioNormal = Number(resumen.bonif.montoNormal)
            return (
            <div className="cli-bonif">
              <div className="cli-bonif-head">
                <div>
                  <div className="cli-bonif-titulo">🎁 Bonificación 3 meses</div>
                  <div className="cli-bonif-sub">
                    Estás en el <strong>mes {resumen.bonif.mesActual} de 3</strong>.
                    {resumen.bonif.mesActual === 1 && (
                      <> Lo extra que pagás este mes (<strong>${(m1 - precioNormal).toLocaleString('es-UY')}</strong>) te lo devolvemos en los meses 2 y 3.</>
                    )}
                    {resumen.bonif.mesActual === 2 && (
                      <> ✅ Esta factura ya viene con <strong>${(resumen.bonif.ahorroTotal/2).toLocaleString('es-UY')}</strong> de descuento. La del mes 3 también.</>
                    )}
                    {resumen.bonif.mesActual === 3 && (
                      <> ✅ Esta factura ya viene con <strong>${(resumen.bonif.ahorroTotal/2).toLocaleString('es-UY')}</strong> de descuento.</>
                    )}
                  </div>
                </div>
              </div>
              <div className="cli-bonif-bar">
                <div className="cli-bonif-bar-fill" style={{ width: `${Math.min(100, (resumen.bonif.mesActual / 3) * 100)}%` }}></div>
              </div>
              <div className="cli-bonif-meses">
                <span className={resumen.bonif.mesActual >= 1 ? 'on' : ''}>Mes 1 · ${m1.toLocaleString('es-UY')}</span>
                <span className={resumen.bonif.mesActual >= 2 ? 'on' : ''}>Mes 2 · ${m23.toLocaleString('es-UY')} 💰</span>
                <span className={resumen.bonif.mesActual >= 3 ? 'on' : ''}>Mes 3 · ${m23.toLocaleString('es-UY')} 💰</span>
              </div>
              <div style={{
                marginTop: 12, padding: '10px 12px',
                background: '#f0f9ff', border: '1px solid #bae6fd',
                borderRadius: 8, fontSize: 12, color: '#075985', lineHeight: 1.5
              }}>
                💡 <strong>Total en 3 meses: ${total3meses.toLocaleString('es-UY')}</strong> (promedio ${Math.round(total3meses / 3).toLocaleString('es-UY')}/mes).
                <br/>
                Desde el <strong>mes 4</strong> pagás el <strong>precio de suscriptor del Kit</strong>: <strong>${precioNormal.toLocaleString('es-UY')}/mes</strong>.
              </div>
            </div>
            )
          })()}

          {/* Detalles */}
          <div className="cli-detalles">
            <div className="cli-detalle">
              <span className="cli-det-tag">Día de entrega</span>
              <span className="cli-det-val" style={{ textTransform: 'capitalize' }}>
                {s.dia_entrega || '—'}
                {s.semana_mes ? ` · ${s.semana_mes}ª semana` : ''}
                {' · '}{s.ventana_horaria === 'tarde' ? 'Tarde' : 'Mañana'}
              </span>
            </div>
            {s.dia_cobro_mes && (
              <div className="cli-detalle">
                <span className="cli-det-tag">Día de cobro mensual</span>
                <span className="cli-det-val">Día {s.dia_cobro_mes}</span>
              </div>
            )}
            <div className="cli-detalle">
              <span className="cli-det-tag">Estado</span>
              <span className="cli-det-val" style={{ textTransform: 'capitalize' }}>
                {s.estado}
                {s.pausa_hasta && <> · hasta {s.pausa_hasta}</>}
              </span>
            </div>
          </div>

          {/* Acciones */}
          {esTitular && s.estado !== 'cancelada' && (
            <div className="cli-acciones">
              <h4>Acciones</h4>
              <div className="cli-acciones-grid">
                {s.estado === 'activa' && (
                  <button onClick={() => onAccion('pausar')}>⏸ Pausar</button>
                )}
                {s.estado === 'pausada' && (
                  <button onClick={() => onAccion('reactivar')} className="cli-acc-go">▶ Reactivar</button>
                )}
                <button onClick={() => onAccion('dia')}>📅 Cambiar día y hora</button>
                <button onClick={() => onAccion('kit')}>📦 Cambiar kit</button>
              </div>
              <button onClick={() => onAccion('cancelar')} className="cli-link-cancelar">
                Cancelar suscripción
              </button>
            </div>
          )}

          {/* Historial */}
          {(pagos.length > 0 || entregas.length > 0) && (
            <div className="cli-historial-grid">
              {entregas.length > 0 && (
                <div>
                  <h4>Últimas entregas</h4>
                  <ul className="cli-historial">
                    {entregas.map(e => (
                      <li key={e.id}>
                        <span>{formatFechaCorta(e.fecha_programada)}</span>
                        <span className="cli-mini-pill" style={{
                          background: e.estado === 'entregada' ? '#dcfce7' : '#f1f5f9',
                          color: e.estado === 'entregada' ? '#15803d' : '#64748b',
                        }}>{e.estado}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {pagos.length > 0 && (
                <div>
                  <h4>Últimos pagos</h4>
                  <ul className="cli-historial">
                    {pagos.map(p => (
                      <li key={p.id}>
                        <span>{formatFechaCorta(p.vence)} · ${Number(p.monto).toLocaleString('es-UY')}</span>
                        <span className="cli-mini-pill" style={{
                          background: p.estado === 'pagado' ? '#dcfce7' : '#fef3c7',
                          color: p.estado === 'pagado' ? '#15803d' : '#92400e',
                        }}>{p.estado}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/* ───────── Modal de acciones (sin cambios funcionales) ───────── */
function ModalAccion({ accion, onClose, onDone }) {
  const { tipo, suscripcion: s } = accion
  if (tipo === 'pausar') return <ModalPausar suscripcion={s} onClose={onClose} onDone={onDone} />
  if (tipo === 'reactivar') return <ModalReactivar suscripcion={s} onClose={onClose} onDone={onDone} />
  if (tipo === 'dia') return <ModalCambiarDia suscripcion={s} onClose={onClose} onDone={onDone} />
  if (tipo === 'kit') return <ModalCambiarKit suscripcion={s} onClose={onClose} onDone={onDone} />
  if (tipo === 'cancelar') return <ModalCancelar suscripcion={s} onClose={onClose} onDone={onDone} />
  if (tipo === 'pagar') return <ModalPagar suscripcion={s} onClose={onClose} />
  return null
}

function ModalPausar({ suscripcion: s, onClose, onDone }) {
  const [hasta, setHasta] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  async function ok() {
    setBusy(true); setErr(null)
    try { await pausarSuscripcion(s.id, hasta || null); onDone() }
    catch (e) { setErr(e.message); setBusy(false) }
  }
  return (
    <ModalShell titulo="Pausar suscripción" onClose={onClose}>
      <p>Mientras esté pausada no te llegan entregas ni se generan cobros.</p>
      <Field label="¿Hasta qué fecha? (opcional, vacío = sin límite)">
        <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} />
      </Field>
      {err && <div className="cli-err-box">{err}</div>}
      <BtnRow onClose={onClose} onOk={ok} okLabel={busy ? 'Pausando…' : 'Pausar'} busy={busy} />
    </ModalShell>
  )
}

function ModalReactivar({ suscripcion: s, onClose, onDone }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  async function ok() {
    setBusy(true); setErr(null)
    try { await reactivarSuscripcion(s.id); onDone() }
    catch (e) { setErr(e.message); setBusy(false) }
  }
  return (
    <ModalShell titulo="Reactivar suscripción" onClose={onClose}>
      <p>Vuelven a llegarte tus pedidos y se reanudan los cobros.</p>
      {err && <div className="cli-err-box">{err}</div>}
      <BtnRow onClose={onClose} onOk={ok} okLabel={busy ? 'Activando…' : 'Reactivar'} busy={busy} />
    </ModalShell>
  )
}

function ModalCambiarDia({ suscripcion: s, onClose, onDone }) {
  const dias = s.barrio_obj?.dias || []
  const [dia, setDia] = useState(s.dia_entrega || dias[0] || '')
  const [ventana, setVentana] = useState(s.ventana_horaria || 'manana')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  async function ok() {
    setBusy(true); setErr(null)
    try { await cambiarDiaSuscripcion(s.id, dia, ventana); onDone() }
    catch (e) { setErr(e.message); setBusy(false) }
  }
  return (
    <ModalShell titulo="Cambiar día y hora de entrega" onClose={onClose}>
      <p>En tu barrio (<strong>{s.barrio_obj?.nombre || s.barrio}</strong>) repartimos los días <strong style={{ textTransform: 'capitalize' }}>{dias.join(', ') || '—'}</strong>.</p>
      <Field label="Día" required>
        <select value={dia} onChange={e => setDia(e.target.value)}>
          {dias.map(d => <option key={d} value={d} style={{ textTransform: 'capitalize' }}>{d}</option>)}
        </select>
      </Field>
      <Field label="Ventana" required>
        <select value={ventana} onChange={e => setVentana(e.target.value)}>
          <option value="manana">Mañana 10-14hs</option>
          <option value="tarde">Tarde 14-18hs</option>
        </select>
      </Field>
      {err && <div className="cli-err-box">{err}</div>}
      <BtnRow onClose={onClose} onOk={ok} okLabel={busy ? 'Guardando…' : 'Guardar cambio'} busy={busy} />
    </ModalShell>
  )
}

function ModalCambiarKit({ suscripcion: s, onClose, onDone }) {
  const [kits, setKits] = useState([])
  const [kitCodigo, setKitCodigo] = useState(s.kit_actual || '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => { getKitsDisponibles().then(setKits) }, [])

  const kitElegido = kits.find(k => k.codigo === kitCodigo)
  const cambioPrecio = (Number(kitElegido?.precio_susc || 0)) - (Number(s.kit?.precio_susc || 0))

  async function ok() {
    setBusy(true); setErr(null)
    try { await cambiarKitSuscripcion(s.id, kitCodigo); onDone() }
    catch (e) { setErr(e.message); setBusy(false) }
  }

  return (
    <ModalShell titulo="Cambiar kit" onClose={onClose}>
      <p>Elegí el kit nuevo. El cambio aplica desde tu próximo pedido.</p>
      <Field label="Kit" required>
        <select value={kitCodigo} onChange={e => setKitCodigo(e.target.value)}>
          {kits.map(k => (
            <option key={k.codigo} value={k.codigo}>
              {k.nombre} · ${Number(k.precio_susc).toLocaleString('es-UY')}/mes ({k.personas})
            </option>
          ))}
        </select>
      </Field>
      {kitElegido && cambioPrecio !== 0 && (
        <div className={cambioPrecio > 0 ? 'cli-info-warn' : 'cli-info-ok'}>
          {cambioPrecio > 0 ? '📈' : '📉'} Cambio mensual: <strong>{cambioPrecio > 0 ? '+' : ''}${cambioPrecio.toLocaleString('es-UY')}</strong>
        </div>
      )}
      {err && <div className="cli-err-box">{err}</div>}
      <BtnRow onClose={onClose} onOk={ok} okLabel={busy ? 'Guardando…' : 'Cambiar kit'} busy={busy} />
    </ModalShell>
  )
}

function ModalCancelar({ suscripcion: s, onClose, onDone }) {
  const [motivo, setMotivo] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  async function ok() {
    if (!confirm('¿Seguro que querés cancelar tu suscripción? Esta acción la podés revertir contactándonos por WhatsApp.')) return
    setBusy(true); setErr(null)
    try { await cancelarMiSuscripcion(s.id, motivo || null); onDone() }
    catch (e) { setErr(e.message); setBusy(false) }
  }
  return (
    <ModalShell titulo="Cancelar suscripción" onClose={onClose}>
      <div className="cli-warn-strong">
        ⚠️ Antes de cancelar, considerá <strong>pausar</strong> tu suscripción si solo necesitás un descanso. Después podés reactivarla cuando quieras.
      </div>
      <Field label="¿Por qué? (opcional, nos ayuda a mejorar)">
        <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows="3" placeholder="Me mudo, voy a estar de viaje, etc." />
      </Field>
      {err && <div className="cli-err-box">{err}</div>}
      <BtnRow onClose={onClose} onOk={ok} okLabel={busy ? 'Cancelando…' : 'Sí, cancelar'} busy={busy} okDanger />
    </ModalShell>
  )
}

function ModalPagar({ suscripcion: s, onClose }) {
  const [cuenta, setCuenta] = useState(null)
  useEffect(() => { getCuentaCobroPublica().then(setCuenta) }, [])
  return (
    <ModalShell titulo="Cómo pagar" onClose={onClose}>
      <p>Por ahora aceptamos solo <strong>transferencia bancaria</strong>. Pronto vas a poder pagar con tarjeta, Abitab o RedPagos desde acá.</p>
      {cuenta?.banco ? (
        <div className="cli-cuenta-box">
          <div className="cli-cuenta-tag">📌 Datos para transferir</div>
          <div className="cli-cuenta-body">
            <strong>Banco:</strong> {cuenta.banco}<br />
            <strong>Cuenta:</strong> {cuenta.numero}<br />
            <strong>Titular:</strong> {cuenta.titular}
            {cuenta.alias && <><br /><strong>Alias:</strong> {cuenta.alias}</>}
          </div>
        </div>
      ) : (
        <div className="cli-warn-strong">
          Datos no configurados. Escribinos al WhatsApp <a href="https://wa.me/59898610001">098 610 001</a>.
        </div>
      )}
      <p className="cli-help-foot">
        Al hacer la transferencia, mandanos el comprobante por WhatsApp <a href="https://wa.me/59898610001">098 610 001</a> y confirmamos tu pago en el día.
      </p>
      <div className="cli-modal-foot">
        <button className="cli-btn-primary" onClick={onClose}>Entendido</button>
      </div>
    </ModalShell>
  )
}

/* ───────── Helpers UI ───────── */
function ModalShell({ titulo, children, onClose }) {
  return (
    <div onClick={onClose} className="cli-modal-bg">
      <div onClick={e => e.stopPropagation()} className="cli-modal">
        <div className="cli-modal-head">
          <h2>{titulo}</h2>
          <button onClick={onClose} className="cli-modal-x" aria-label="Cerrar">✕</button>
        </div>
        {children}
      </div>
    </div>
  )
}
function BtnRow({ onClose, onOk, okLabel, busy, okDanger }) {
  return (
    <div className="cli-modal-foot">
      <button className="cli-btn-ghost" onClick={onClose}>Cancelar</button>
      <button className={okDanger ? 'cli-btn-danger' : 'cli-btn-primary'} onClick={onOk} disabled={busy}>{okLabel}</button>
    </div>
  )
}
function Field({ label, children, required }) {
  return (
    <label className="cli-field">
      <b>{label} {required && <span className="cli-req">*</span>}</b>
      {children}
    </label>
  )
}
function formatFecha(d) {
  if (!d) return '—'
  return new Date(d + 'T12:00:00').toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'long' })
}
function formatFechaCorta(d) {
  if (!d) return '—'
  return new Date(d + 'T12:00:00').toLocaleDateString('es-UY', { day: 'numeric', month: 'short', year: '2-digit' })
}

const styles = `
:root {
  --navy:  #1a3461;
  --blue:  #1a5cb8;
  --teal:  #00c4d4;
  --cream: #F8FAFC;
  --line2: #e2e8f0;
  --muted2:#64748b;
}

html, body { background: var(--cream); margin: 0 }
.cli-app {
  min-height: 100vh;
  background: var(--cream);
  font-family: 'Inter', system-ui, sans-serif;
  color: var(--navy);
  -webkit-font-smoothing: antialiased;
  display: flex; flex-direction: column;
}

/* ─── Stats arriba (3 cards) ─── */
.cli-stats {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 20px;
}
@media (min-width: 720px) { .cli-stats { grid-template-columns: 1fr 1fr 1fr } }
.cli-stat {
  background: #fff; border-radius: 14px; padding: 16px 18px;
  border: 1px solid var(--line2);
  box-shadow: 0 4px 16px rgba(26,52,97,.04);
}
.cli-stat.is-warning { background: linear-gradient(180deg, #fffbeb 0%, #fef3c7 100%); border-color: #fde68a; }
.cli-stat.is-savings { background: linear-gradient(180deg, #f0fdf4 0%, #dcfce7 100%); border-color: #86efac; }
.cli-stat-tag {
  font-size: 11px; font-weight: 700; letter-spacing: .04em;
  text-transform: uppercase; color: var(--muted2); margin-bottom: 6px;
}
.cli-stat.is-warning .cli-stat-tag { color: #92400e; }
.cli-stat.is-savings .cli-stat-tag { color: #15803d; }
.cli-stat-big {
  font-size: 22px; font-weight: 800; color: var(--navy);
  line-height: 1.1; text-transform: capitalize;
}
.cli-stat.is-warning .cli-stat-big { color: #78350f; }
.cli-stat.is-savings .cli-stat-big { color: #15803d; }
.cli-stat-sub {
  font-size: 12px; color: var(--muted2); margin-top: 4px; line-height: 1.4;
}

/* ─── Tabs ─── */
.cli-tabs {
  display: flex; gap: 4px;
  background: #fff; border: 1px solid var(--line2); border-radius: 12px;
  padding: 4px; margin-bottom: 20px;
  overflow-x: auto;
}
.cli-tab {
  flex: 1; padding: 10px 14px;
  background: transparent; border: 0;
  font-family: inherit; font-size: 13px; font-weight: 600;
  color: var(--muted2); cursor: pointer; border-radius: 8px;
  white-space: nowrap;
  transition: background .15s, color .15s;
}
.cli-tab:hover { background: #f9fafb; }
.cli-tab.is-active {
  background: var(--navy); color: #fff;
}

.cli-tab-content { min-height: 200px; }
.cli-tab-pane { padding: 0; }
.cli-pane-h2 {
  font-size: 22px; font-weight: 800; color: var(--navy); margin: 0 0 4px;
}
.cli-pane-sub {
  color: var(--muted2); font-size: 14px; margin: 0 0 20px;
}

/* ─── Navbar ─── */
.cli-nav {
  position: sticky; top: 0; z-index: 50;
  background: rgba(26,52,97,.94);
  backdrop-filter: blur(12px);
}
.cli-nav-inner {
  max-width: 1100px; margin: 0 auto;
  padding: 0 16px; height: 64px;
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px;
}
.cli-logo {
  display: flex; align-items: center; gap: 10px;
  text-decoration: none; color: #fff;
}
.cli-logo-iso {
  width: 32px; height: 32px;
  background: #fff; border-radius: 8px; padding: 3px;
  box-sizing: border-box;
  object-fit: contain;
}
.cli-logo span {
  font-size: 22px; font-weight: 900;
  letter-spacing: -.02em;
}
.cli-logo em { font-style: normal; color: var(--teal) }

.cli-nav-actions { display: flex; gap: 10px; align-items: center }
.cli-nav-link {
  color: rgba(255,255,255,.78); text-decoration: none;
  font-size: 13px; font-weight: 600;
  padding: 8px 12px; border-radius: 999px;
  transition: background .15s, color .15s;
}
.cli-nav-link:hover { background: rgba(255,255,255,.1); color: var(--teal) }
.cli-nav-logout {
  background: rgba(255,255,255,.12); color: #fff;
  border: 0; padding: 8px 14px; border-radius: 999px;
  font-size: 13px; font-weight: 600; cursor: pointer;
  font-family: inherit;
}
.cli-nav-logout:hover { background: rgba(255,255,255,.2) }

/* ─── Hero ─── */
.cli-hero {
  background: linear-gradient(135deg, #1a3461 0%, #1a5cb8 60%, #00c4d4 100%);
  color: #fff;
  padding: 48px 16px 56px;
}
.cli-hero-inner { max-width: 760px; margin: 0 auto; text-align: center }
.cli-hero-pill {
  display: inline-flex; align-items: center; gap: 8px;
  background: rgba(255,255,255,.1);
  border: 1px solid rgba(255,255,255,.18);
  padding: 6px 14px; border-radius: 999px;
  font-size: 11px; font-weight: 700; letter-spacing: .06em;
  color: rgba(255,255,255,.92);
  margin-bottom: 14px; text-transform: uppercase;
}
.cli-hero-dot {
  width: 7px; height: 7px; background: var(--teal); border-radius: 50%;
  animation: pulse 1.6s ease-in-out infinite;
}
@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }

.cli-hero-h1 {
  font-size: clamp(28px, 5vw, 44px); font-weight: 900;
  line-height: 1.1; letter-spacing: -.02em; margin: 0 0 12px;
}
.cli-hero-h1 span { color: var(--teal) }
.cli-hero-sub {
  font-size: clamp(14px, 2.4vw, 16px);
  color: rgba(255,255,255,.82); line-height: 1.5;
  max-width: 540px; margin: 0 auto;
}

/* ─── Main ─── */
.cli-main {
  flex: 1;
  max-width: 760px; width: 100%;
  margin: 0 auto;
  padding: 28px 16px 60px;
  box-sizing: border-box;
}
.cli-loading {
  text-align: center; padding: 60px 20px;
  font-size: 14px; color: var(--muted2);
}
.cli-list { display: flex; flex-direction: column; gap: 14px }

/* ─── Card vacía (sin suscripciones) ─── */
.cli-vacio {
  text-align: center; padding: 28px 20px;
  background: #fff; border-radius: 20px;
  box-shadow: 0 8px 40px rgba(26,52,97,.08);
}
.cli-vacio-iso {
  width: 80px; height: 80px;
  margin: 0 auto 18px;
  background: #fff;
  border: 2px dashed var(--teal);
  border-radius: 24px;
  display: flex; align-items: center; justify-content: center;
}
.cli-vacio-iso img { width: 56px; height: 56px; object-fit: contain }
.cli-vacio h2 {
  color: var(--navy); font-size: 26px; font-weight: 900;
  letter-spacing: -.01em; margin: 0 0 12px;
}
.cli-vacio p {
  font-size: 15px; color: var(--muted2); line-height: 1.5;
  max-width: 460px; margin: 0 auto 24px;
}
.cli-vacio-help {
  font-size: 13px; color: var(--muted2); margin-top: 18px !important;
}
.cli-vacio-help a { color: var(--blue); font-weight: 700 }
.cli-cta {
  display: inline-block; text-decoration: none;
  background: linear-gradient(135deg, var(--blue) 0%, var(--teal) 100%);
  color: #fff; font-weight: 800; font-size: 15px;
  padding: 14px 28px; border-radius: 999px;
  box-shadow: 0 6px 20px rgba(0,196,212,.3);
  transition: transform .15s, box-shadow .15s;
}
.cli-cta:hover { transform: translateY(-2px); box-shadow: 0 10px 26px rgba(0,196,212,.4) }

/* ─── Card de suscripción ─── */
.cli-card {
  background: #fff; border-radius: 20px;
  box-shadow: 0 8px 40px rgba(26,52,97,.08);
  overflow: hidden;
  transition: box-shadow .2s;
}
.cli-card.is-open { box-shadow: 0 12px 48px rgba(26,52,97,.12) }
.cli-card-head {
  width: 100%;
  display: flex; gap: 14px; align-items: flex-start;
  padding: 20px 22px; border: 0; background: transparent;
  text-align: left; cursor: pointer; font-family: inherit;
  transition: background .15s;
}
.cli-card-head:hover { background: #f8fafc }
.cli-card-head-left { flex: 1; min-width: 0 }
.cli-card-head-right { flex-shrink: 0; text-align: right }

.cli-alias {
  font-size: 11px; font-weight: 800;
  color: var(--teal); letter-spacing: .08em;
  margin-bottom: 4px;
}
.cli-card-titulo {
  font-size: 20px; font-weight: 900; color: var(--navy);
  margin: 0 0 4px; letter-spacing: -.01em;
}
.cli-card-direccion {
  font-size: 13px; color: var(--muted2); line-height: 1.4;
}
.cli-card-beneficio {
  font-size: 11px; color: #7c3aed; margin-top: 6px;
  font-weight: 700;
}
.cli-pill {
  display: inline-block;
  padding: 4px 12px; border-radius: 999px;
  font-size: 11px; font-weight: 800;
  text-transform: uppercase; letter-spacing: .05em;
}
.cli-card-precio {
  font-size: 22px; font-weight: 900; color: var(--navy);
  margin-top: 10px; letter-spacing: -.01em;
}
.cli-card-precio span {
  font-size: 11px; color: var(--muted2); font-weight: 600;
}
.cli-card-toggle {
  font-size: 14px; color: var(--muted2); margin-top: 8px;
}

.cli-card-body {
  padding: 0 22px 22px;
  border-top: 1px solid var(--line2);
}

/* Bloques próxima entrega + cobro */
.cli-blocks-2 {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px;
  margin-top: 18px;
}
@media (max-width: 900px) { .cli-blocks-2 { grid-template-columns: 1fr 1fr } }
@media (max-width: 600px) { .cli-blocks-2 { grid-template-columns: 1fr } }
.cli-block.is-savings {
  background: linear-gradient(135deg, #f0fdf4, #ecfdf5);
  border: 1.5px solid #86efac;
}
.cli-block.is-savings .cli-block-tag { color: #14532d; font-weight: 800 }
.cli-block.is-savings .cli-block-big { color: #14532d }

.cli-block {
  padding: 14px 16px;
  background: #f8fafc; border: 1px solid var(--line2);
  border-radius: 12px;
}
.cli-block.is-warning {
  background: #fef3c7; border-color: #fcd34d;
}
.cli-block.is-ok {
  background: #f0fdf4; border-color: #bbf7d0;
}
.cli-block-tag {
  font-size: 10px; font-weight: 800;
  letter-spacing: .12em; text-transform: uppercase;
  color: var(--navy); margin-bottom: 6px;
}
.cli-block.is-warning .cli-block-tag { color: #92400e }
.cli-block.is-ok .cli-block-tag { color: #15803d }
.cli-block-big {
  font-size: 18px; font-weight: 900; color: var(--navy);
  line-height: 1.2; text-transform: capitalize;
}
.cli-block.is-warning .cli-block-big { color: #92400e; text-transform: none }
.cli-block-sub {
  font-size: 13px; color: var(--muted2); margin-top: 4px;
}
.cli-block.is-warning .cli-block-sub { color: #92400e }
.cli-block-extra {
  font-size: 11px; color: var(--muted2); margin-top: 4px;
  font-family: ui-monospace, monospace;
}
.cli-block-extra-info {
  font-size: 11px; color: var(--muted2); margin-top: 6px;
  font-style: italic;
}
.cli-block-placeholder {
  font-size: 13px; color: var(--muted2); margin-top: 6px;
  font-style: italic;
}
.cli-corte {
  margin-top: 10px; padding: 8px 12px;
  background: rgba(0,196,212,.12); color: #0e7490;
  border-radius: 8px; font-size: 12px; font-weight: 700;
  border-left: 3px solid var(--teal);
}
.cli-corte.is-urgent {
  background: #fef3c7; color: #92400e;
  border-left-color: #f59e0b;
}
.cli-btn-pagar {
  margin-top: 10px;
  padding: 8px 14px;
  background: #92400e; color: #fff;
  border: 0; border-radius: 999px;
  font-weight: 700; font-size: 13px;
  cursor: pointer; font-family: inherit;
}
.cli-btn-pagar:hover { background: #78350f }

/* Detalles */
.cli-detalles {
  margin-top: 18px; padding-top: 14px;
  border-top: 1px dashed var(--line2);
  display: flex; flex-direction: column;
}
.cli-detalle {
  display: flex; justify-content: space-between; align-items: center;
  font-size: 13px; padding: 8px 0;
}
.cli-det-tag { color: var(--muted2); font-weight: 600 }
.cli-det-val { font-weight: 700; color: var(--navy); text-align: right }

/* Acciones */
.cli-acciones {
  margin-top: 20px; padding-top: 16px;
  border-top: 1px dashed var(--line2);
}
.cli-acciones h4 {
  margin: 0 0 12px; font-size: 11px;
  color: var(--muted2); letter-spacing: .12em;
  text-transform: uppercase; font-weight: 800;
}
.cli-acciones-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 10px;
}
.cli-acciones-grid button {
  padding: 14px 12px;
  background: #fff; border: 1.5px solid var(--line2);
  border-radius: 12px;
  font-weight: 700; font-size: 13px; color: var(--navy);
  cursor: pointer; text-align: center; font-family: inherit;
  transition: border-color .15s, background .15s;
}
.cli-acciones-grid button:hover {
  border-color: var(--teal); background: #f0fcfd;
}
.cli-acc-go {
  background: linear-gradient(135deg, var(--blue), var(--teal)) !important;
  color: #fff !important; border-color: transparent !important;
}
.cli-acc-go:hover { color: #fff !important; opacity: .92 }

.cli-link-cancelar {
  display: block; width: 100%; margin: 14px auto 0;
  background: transparent; border: 0;
  color: var(--muted2);
  font-size: 12px; font-weight: 600;
  text-decoration: underline; cursor: pointer;
  font-family: inherit; padding: 6px;
}
.cli-link-cancelar:hover { color: #991b1b }

/* Historial */
.cli-historial-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
  margin-top: 22px; padding-top: 16px;
  border-top: 1px dashed var(--line2);
}
@media (max-width: 600px) { .cli-historial-grid { grid-template-columns: 1fr } }
.cli-historial-grid h4 {
  margin: 0 0 8px; font-size: 11px;
  color: var(--muted2); letter-spacing: .12em;
  text-transform: uppercase; font-weight: 800;
}
.cli-historial { list-style: none; padding: 0; margin: 0; max-height: 240px; overflow-y: auto }
.cli-historial li {
  display: flex; justify-content: space-between; align-items: center;
  padding: 10px 0; border-bottom: 1px solid var(--line2);
  font-size: 13px;
}
.cli-historial li:last-child { border-bottom: 0 }
.cli-mini-pill {
  padding: 2px 9px; border-radius: 999px;
  font-size: 10px; font-weight: 800;
  text-transform: uppercase; letter-spacing: .04em;
}

/* ─── Bloque Bonificación 3 meses ─── */
.cli-bonif {
  margin-top: 18px;
  padding: 16px 18px;
  background: linear-gradient(135deg, rgba(0,196,212,.08) 0%, rgba(26,92,184,.08) 100%);
  border: 1px solid rgba(0,196,212,.3);
  border-radius: 14px;
}
.cli-bonif-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px }
.cli-bonif-titulo {
  font-size: 14px; font-weight: 800; color: var(--navy);
  letter-spacing: -.005em;
}
.cli-bonif-sub {
  font-size: 13px; color: #334155; margin-top: 4px; line-height: 1.5;
}
.cli-bonif-bar {
  margin: 14px 0 8px;
  background: rgba(0,0,0,.06); border-radius: 999px;
  height: 8px; overflow: hidden;
}
.cli-bonif-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--blue) 0%, var(--teal) 100%);
  border-radius: 999px;
  transition: width .4s ease;
}
.cli-bonif-meses {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px;
  margin-top: 6px;
}
.cli-bonif-meses span {
  font-size: 10px; font-weight: 700;
  color: var(--muted2); text-align: center;
  padding: 4px 6px; border-radius: 6px;
  background: rgba(255,255,255,.5);
  letter-spacing: .02em;
}
.cli-bonif-meses span.on { color: var(--navy); background: #fff }

/* ─── Bloque Vecinos ─── */
.cli-vecinos {
  margin-top: 14px;
  padding: 16px 18px;
  background: #fff;
  border: 1px solid var(--line2);
  border-radius: 14px;
}
.cli-vecinos-titulo {
  font-size: 14px; font-weight: 800; color: var(--navy);
  letter-spacing: -.005em;
}
.cli-vecinos-sub {
  font-size: 13px; color: #334155; margin-top: 4px; line-height: 1.5;
}
.cli-vecinos-bar {
  margin: 14px 0 6px;
  background: #f1f5f9; border-radius: 999px;
  height: 10px; overflow: hidden;
  position: relative;
}
.cli-vecinos-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #16a34a 0%, var(--teal) 100%);
  border-radius: 999px;
  transition: width .4s ease;
}
.cli-vecinos-num {
  font-size: 12px; color: var(--muted2); font-weight: 700;
  text-align: right; margin-bottom: 8px;
}
.cli-vecinos-cta {
  display: block; width: 100%;
  margin-top: 10px; padding: 11px 16px;
  background: #25d366; color: #fff;
  border-radius: 999px;
  font-weight: 800; font-size: 13px;
  text-align: center; text-decoration: none;
  box-shadow: 0 4px 14px rgba(37,211,102,.3);
  transition: transform .12s, box-shadow .12s;
}
.cli-vecinos-cta:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(37,211,102,.45);
}

/* ─── Bloque "Mis pedidos" (one-shot: Tienda + Kit suelto) ─── */
.cli-pedidos-block { margin-top: 32px }
.cli-pedidos-block h2 {
  font-size: 22px; font-weight: 900; color: var(--navy);
  letter-spacing: -.01em; margin: 0 0 14px;
}
.cli-pedidos-list { display: flex; flex-direction: column; gap: 10px }
.cli-pedido {
  background: #fff; border: 1px solid var(--line2); border-radius: 14px;
  box-shadow: 0 2px 8px rgba(26,52,97,.04);
  transition: border-color .15s, box-shadow .15s;
  overflow: hidden;
}
.cli-pedido:hover { border-color: var(--teal); box-shadow: 0 4px 16px rgba(26,52,97,.08) }
.cli-pedido.is-open { border-color: var(--teal); box-shadow: 0 6px 22px rgba(26,52,97,.10) }
.cli-pedido-head {
  width: 100%; box-sizing: border-box;
  display: flex; justify-content: space-between; align-items: center; gap: 14px;
  background: transparent; border: 0;
  padding: 14px 18px;
  cursor: pointer; font-family: inherit; text-align: left; color: inherit;
}
.cli-pedido-head:hover { background: #f8fafc }
.cli-pedido-info { flex: 1; min-width: 0 }
.cli-pedido-toggle { font-size: 14px; color: var(--muted2); margin-top: 2px }
.cli-pedido-body {
  padding: 0 18px 16px; border-top: 1px dashed var(--line2);
  display: flex; flex-direction: column; gap: 14px;
}
.cli-pedido-section { padding-top: 12px }
.cli-pedido-section-tag {
  font-size: 10px; font-weight: 800; color: var(--muted2);
  letter-spacing: .08em; text-transform: uppercase; margin-bottom: 6px;
}
.cli-pedido-items { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 6px }
.cli-pedido-items li {
  display: flex; gap: 10px; align-items: baseline;
  font-size: 13px; color: var(--navy);
  padding: 6px 0; border-bottom: 1px dashed rgba(26,52,97,.06);
}
.cli-pedido-items li:last-child { border-bottom: 0 }
.cli-pedido-item-qty { font-weight: 800; color: var(--teal); flex-shrink: 0; min-width: 24px }
.cli-pedido-item-name { flex: 1 }
.cli-pedido-item-pres { color: var(--muted2); font-size: 12px }
.cli-pedido-item-precio { font-weight: 700; color: var(--navy); white-space: nowrap }
.cli-pedido-kit { font-size: 14px; color: var(--navy); padding: 8px 0 }
.cli-pedido-meta { font-size: 13px; color: var(--navy); line-height: 1.5 }
.cli-pedido-totales { display: flex; flex-direction: column; gap: 4px; font-size: 13px; color: var(--navy) }
.cli-pedido-totales > div { display: flex; justify-content: space-between }
.cli-pedido-totales > div span:first-child { color: var(--muted2) }
.cli-pedido-total {
  border-top: 1.5px solid var(--navy); padding-top: 6px; margin-top: 4px;
  font-weight: 900; font-size: 15px;
}
.cli-pedido-total span:first-child { color: var(--navy) !important; font-size: 11px; text-transform: uppercase; letter-spacing: .06em }
.cli-pedido-pagar {
  background: #FEF3C7; border: 1px solid #FCD34D; color: #78350F;
  padding: 10px 12px; border-radius: 10px; font-size: 12px; line-height: 1.5;
}
.cli-pedido-pagar a { color: #92400E; font-weight: 800 }
.cli-pedido-num {
  font-size: 11px; font-weight: 800; color: var(--teal);
  letter-spacing: .08em; text-transform: uppercase;
}
.cli-pedido-resumen {
  font-size: 14px; font-weight: 700; color: var(--navy);
  margin-top: 4px;
}
.cli-pedido-fecha {
  font-size: 12px; color: var(--muted2); margin-top: 4px;
}
.cli-pedido-right {
  display: flex; flex-direction: column; align-items: flex-end; gap: 8px;
  flex-shrink: 0;
}
.cli-pedido-monto {
  font-size: 17px; font-weight: 900; color: var(--navy);
  letter-spacing: -.01em;
}

/* ─── Bloque "Comprá extras" ─── */
.cli-extras { margin-top: 24px }
.cli-extras-h2 {
  font-size: 18px; font-weight: 900; color: var(--navy);
  letter-spacing: -.01em; margin: 0 0 4px;
}
.cli-extras-sub {
  font-size: 12px; color: var(--muted2); margin: 0 0 12px;
}
.cli-extras-grid {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px;
}
@media (max-width: 760px) { .cli-extras-grid { grid-template-columns: 1fr } }
.cli-extras-card {
  background: #fff; border-radius: 12px;
  box-shadow: 0 2px 10px rgba(26,52,97,.06);
  border: 1px solid rgba(26,52,97,.06);
  padding: 14px 16px;
  text-decoration: none; color: var(--navy);
  display: flex; flex-direction: column; gap: 4px;
  transition: transform .15s, box-shadow .15s, border-color .15s;
}
.cli-extras-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 22px rgba(26,52,97,.1);
  border-color: var(--teal);
}
.cli-extras-icon { font-size: 24px; line-height: 1 }
.cli-extras-titulo { font-size: 14px; font-weight: 800; color: var(--navy); margin-top: 2px }
.cli-extras-desc {
  font-size: 11.5px; color: var(--muted2); line-height: 1.4;
  flex: 1;
}
.cli-extras-cta {
  font-size: 12px; font-weight: 800; color: var(--teal);
  margin-top: 4px;
}

/* ─── Modal ─── */
.cli-modal-bg {
  position: fixed; inset: 0;
  background: rgba(15,31,61,.55); backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
  animation: fadeIn .15s ease;
}
@keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
.cli-modal {
  background: #fff; border-radius: 18px;
  padding: 28px 24px 24px;
  max-width: 460px; width: 100%;
  max-height: 90vh; overflow-y: auto;
  box-shadow: 0 24px 60px rgba(0,0,0,.35);
  font-family: 'Inter', system-ui, sans-serif;
}
.cli-modal-head {
  display: flex; justify-content: space-between; align-items: flex-start;
  gap: 12px; margin-bottom: 14px;
}
.cli-modal-head h2 {
  margin: 0; font-size: 19px; font-weight: 900;
  color: var(--navy); letter-spacing: -.01em;
}
.cli-modal-x {
  background: #f1f5f9; border: 0; border-radius: 50%;
  width: 32px; height: 32px; cursor: pointer;
  font-size: 14px; color: var(--muted2);
  flex-shrink: 0;
}
.cli-modal-x:hover { background: #e2e8f0; color: var(--navy) }

.cli-modal p {
  font-size: 14px; color: #334155; line-height: 1.5; margin: 0 0 14px;
}
.cli-modal input, .cli-modal select, .cli-modal textarea {
  width: 100%; box-sizing: border-box;
  padding: 11px 13px; font-size: 14px;
  border: 1.5px solid var(--line2); border-radius: 10px;
  background: #fff; color: var(--navy); font-family: inherit;
  transition: border-color .15s, box-shadow .15s;
}
.cli-modal input:focus, .cli-modal select:focus, .cli-modal textarea:focus {
  outline: none; border-color: var(--teal);
  box-shadow: 0 0 0 3px rgba(0,196,212,.15);
}

.cli-field { display: block; margin-bottom: 12px }
.cli-field b {
  display: block; font-size: 11px; font-weight: 800;
  color: var(--navy); letter-spacing: .08em;
  text-transform: uppercase; margin-bottom: 6px;
}
.cli-req { color: #dc2626; margin-left: 2px }

.cli-modal-foot {
  display: flex; gap: 10px; justify-content: flex-end;
  margin-top: 18px;
}
.cli-btn-primary, .cli-btn-ghost, .cli-btn-danger {
  padding: 11px 20px; font-size: 14px; font-weight: 700;
  border-radius: 999px; cursor: pointer; font-family: inherit;
  border: 0;
  transition: opacity .15s, transform .12s;
}
.cli-btn-primary {
  background: linear-gradient(135deg, var(--blue), var(--teal));
  color: #fff;
  box-shadow: 0 4px 14px rgba(0,196,212,.25);
}
.cli-btn-primary:hover:not(:disabled) { transform: translateY(-1px) }
.cli-btn-ghost {
  background: transparent; color: var(--muted2);
}
.cli-btn-ghost:hover { background: #f1f5f9; color: var(--navy) }
.cli-btn-danger {
  background: #dc2626; color: #fff;
}
.cli-btn-danger:hover:not(:disabled) { background: #b91c1c }
.cli-btn-primary:disabled, .cli-btn-danger:disabled { opacity: .5; cursor: wait }

.cli-err-box {
  background: #fef2f2; border: 1px solid #fca5a5;
  color: #991b1b; border-radius: 10px;
  padding: 10px 14px; font-size: 13px; margin-top: 10px;
}
.cli-info-warn {
  background: #fef3c7; border: 1px solid #fcd34d;
  color: #92400e; border-radius: 10px;
  padding: 10px 14px; font-size: 13px; margin-bottom: 10px;
}
.cli-info-ok {
  background: #f0fdf4; border: 1px solid #bbf7d0;
  color: #15803d; border-radius: 10px;
  padding: 10px 14px; font-size: 13px; margin-bottom: 10px;
}
.cli-warn-strong {
  background: #fef2f2; border: 1.5px solid #fca5a5;
  border-radius: 10px; padding: 12px 14px;
  font-size: 13px; color: #991b1b; margin-bottom: 14px;
  line-height: 1.5;
}
.cli-cuenta-box {
  background: #f0fcfd; border: 1px solid #b3eef3;
  border-radius: 12px; padding: 14px 16px; margin-top: 12px;
}
.cli-cuenta-tag {
  font-size: 11px; font-weight: 800;
  letter-spacing: .08em; text-transform: uppercase;
  color: var(--navy); margin-bottom: 8px;
}
.cli-cuenta-body { font-size: 14px; line-height: 1.7; color: var(--navy) }
.cli-cuenta-body strong { font-weight: 700 }
.cli-help-foot {
  font-size: 13px; color: var(--muted2);
  margin-top: 16px !important; line-height: 1.5;
}
.cli-help-foot a { color: var(--blue); font-weight: 700 }

/* ─── WhatsApp flotante ─── */
.cli-wa {
  position: fixed; bottom: 24px; right: 24px; z-index: 60;
  background: #25d366; border-radius: 50%;
  width: 56px; height: 56px;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 20px rgba(37,211,102,.45);
  transition: transform .15s;
  text-decoration: none;
}
.cli-wa:hover { transform: scale(1.08) }

/* ─── Footer ─── */
.cli-footer { background: var(--navy); padding: 48px 16px 32px }
.cli-footer-inner { max-width: 1100px; margin: 0 auto }
.cli-footer-grid {
  display: grid; grid-template-columns: 1fr;
  gap: 32px; margin-bottom: 32px;
}
@media (min-width: 640px) {
  .cli-footer-grid { grid-template-columns: 1.4fr 1fr 1fr }
}
.cli-footer-logo {
  font-size: 22px; font-weight: 900; color: #fff;
  margin-bottom: 10px; letter-spacing: -.02em;
}
.cli-footer-logo span { color: var(--teal) }
.cli-footer-desc {
  color: rgba(255,255,255,.5); font-size: 13px;
  line-height: 1.6; margin: 0;
}
.cli-footer-tit {
  color: #fff; font-weight: 700;
  font-size: 12px; text-transform: uppercase; letter-spacing: .08em;
  margin-bottom: 14px;
}
.cli-footer-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px }
.cli-footer-list a {
  color: rgba(255,255,255,.6); font-size: 13px;
  text-decoration: none; transition: color .15s;
}
.cli-footer-list a:hover { color: var(--teal) }
.cli-footer-bot {
  border-top: 1px solid rgba(255,255,255,.1);
  padding-top: 26px;
  display: flex; flex-direction: column; gap: 6px;
  align-items: center; text-align: center;
}
@media (min-width: 640px) {
  .cli-footer-bot { flex-direction: row; justify-content: space-between; text-align: left }
}
.cli-footer-bot p { margin: 0 }
.cli-footer-bot p:first-child { color: rgba(255,255,255,.4); font-size: 13px }
.cli-footer-bot p:last-child { color: rgba(255,255,255,.3); font-size: 11px }
`
